/**
 * @fileoverview Gestion MFA du compte agent.
 *
 * Présente le parcours complet : état du facteur TOTP, configuration d'une
 * application d'authentification, vérification du premier code, récupération
 * des codes de secours et demande de réinitialisation.
 */

import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyButton,
  FormField,
  InfoNotice,
  PageHeader,
  Progress,
  QrCode,
  ReasonTextarea,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SuccessBanner,
  TotpInput,
  WarningNotice,
  useCopyToClipboard,
} from '@gnpc/ui';
import { Check, Copy, RefreshCcw, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { AccountNav } from '../features/account/AccountNav.tsx';
import { useCurrentUser } from '../features/auth/CapabilityProvider.tsx';

type MfaSetupStep = 'scan' | 'verify' | 'backup';

/**
 * Codes de secours mockés — 10 codes déterministes au format XXXX-XXXX,
 * conformément au contrat (jeu complet généré à l'activation du MFA).
 * Affichés une seule fois : une fois confirmés par l'agent, ils ne sont
 * plus jamais réaffichés dans la console.
 */
const BACKUP_CODES = [
  'G7K9-P2LM',
  'AH42-Q9SD',
  'N8P3-Z6VT',
  'M2CX-8BA1',
  'T5RW-K3JN',
  'B9DF-L7QU',
  'X4HS-W2EM',
  'C6VY-R8PT',
  'J3NA-D5KZ',
  'Q8UB-F4GW',
];

function getSetupProgress(step: MfaSetupStep): number {
  if (step === 'scan') return 34;
  if (step === 'verify') return 67;
  return 100;
}

export function AccountMfaPage(): JSX.Element {
  const { user } = useCurrentUser();
  /** MFA obligatoire pour les rôles financiers (C-30 / C-37). */
  const mfaMandatory = ['ADMIN', 'FINANCE_VALIDATOR', 'SUPERVISOR'].includes(user.role);
  const [setupSheetOpen, setSetupSheetOpen] = useState(false);
  const [resetSheetOpen, setResetSheetOpen] = useState(false);
  const [setupStep, setSetupStep] = useState<MfaSetupStep>('scan');
  const [totp, setTotp] = useState('');
  const [resetReason, setResetReason] = useState("Perte d'accès à l'application MFA.");
  /**
   * Masquage définitif de la liste des codes de secours. Volontairement
   * jamais remis à `false` (même à la fermeture du sheet) : les codes ne
   * sont affichés qu'une seule fois.
   */
  const [backupCodesAcknowledged, setBackupCodesAcknowledged] = useState(false);
  /**
   * Copie groupée des 10 codes (un par ligne) — complète les `<CopyButton>`
   * unitaires : l'agent archive le jeu complet en un clic dans son
   * gestionnaire de mots de passe.
   */
  const { copy: copyAllBackupCodes, copied: allBackupCodesCopied } = useCopyToClipboard();

  const resetSetupFlow = (open: boolean) => {
    setSetupSheetOpen(open);
    if (!open) {
      setSetupStep('scan');
      setTotp('');
    }
  };

  return (
    <>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Compte agent"
          title="MFA et codes de secours"
          description="Configurer l'authentification forte et préparer les méthodes de récupération du compte."
          actions={
            <div className="flex flex-wrap gap-2">
              <Sheet open={setupSheetOpen} onOpenChange={resetSetupFlow}>
                <SheetTrigger asChild>
                  <Button>
                    <Smartphone className="size-4" />
                    Configurer une application MFA
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
                  <SheetHeader className="border-border border-b p-5 pr-12">
                    <SheetTitle>Configurer le MFA</SheetTitle>
                    <SheetDescription>
                      Scannez le QR code dans votre application d'authentification, confirmez un code, puis conservez les codes de secours.
                    </SheetDescription>
                  </SheetHeader>

                  <div className="grid gap-5 p-5">
                    <Progress
                      value={getSetupProgress(setupStep)}
                      label={
                        setupStep === 'scan'
                          ? 'Étape 1 sur 3'
                          : setupStep === 'verify'
                            ? 'Étape 2 sur 3'
                            : 'Étape 3 sur 3'
                      }
                      showValue
                    />

                    {setupStep === 'scan' ? (
                      <>
                        <div className="flex justify-center rounded-lg border border-dashed p-5">
                          <QrCode
                            value={`otpauth://totp/GNPC:${user.email}?secret=JBSWY3DPEHPK3PXP&issuer=GNPC`}
                            label="Scanner avec Google Authenticator"
                            description="Applications compatibles TOTP acceptées."
                          />
                        </div>
                        <InfoNotice title="Après le scan">
                          L'application affichera un code à 6 chiffres à confirmer à l'étape suivante.
                        </InfoNotice>
                      </>
                    ) : null}

                    {setupStep === 'verify' ? (
                      <>
                        <FormField label="Code de vérification" required>
                          <TotpInput value={totp} onChange={setTotp} />
                        </FormField>
                        <WarningNotice title="Tolérance de temps">
                          Si le code expire, attendez le code suivant dans votre application.
                        </WarningNotice>
                      </>
                    ) : null}

                    {setupStep === 'backup' ? (
                      <>
                        <SuccessBanner title="Application MFA vérifiée">
                          Conservez ces codes hors de la console. Chaque code ne peut servir qu'une seule fois.
                        </SuccessBanner>
                        {backupCodesAcknowledged ? (
                          <WarningNotice title="Codes masqués définitivement">
                            Les codes de secours ne seront plus jamais réaffichés. En cas de
                            perte, demandez une réinitialisation MFA à un administrateur.
                          </WarningNotice>
                        ) : (
                          <>
                            <WarningNotice title="Affichés une seule fois">
                              Ces codes ne seront plus jamais réaffichés après confirmation.
                              Enregistrez-les dans un emplacement sûr avant de continuer.
                            </WarningNotice>
                            <div className="grid gap-2 rounded-lg border border-border p-4">
                              {BACKUP_CODES.map((code) => (
                                <div key={code} className="flex items-center justify-between gap-3">
                                  <code className="text-quipux-navy-950 font-mono text-sm">{code}</code>
                                  <CopyButton value={code} ariaLabel={`Copier le code ${code}`} />
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-1"
                                onClick={() => void copyAllBackupCodes(BACKUP_CODES.join('\n'))}
                              >
                                {allBackupCodesCopied ? (
                                  <Check className="size-4" aria-hidden="true" />
                                ) : (
                                  <Copy className="size-4" aria-hidden="true" />
                                )}
                                {allBackupCodesCopied ? 'Codes copiés' : 'Copier les 10 codes'}
                              </Button>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => setBackupCodesAcknowledged(true)}
                            >
                              <ShieldCheck className="size-4" />
                              J'ai enregistré mes codes
                            </Button>
                          </>
                        )}
                      </>
                    ) : null}
                  </div>

                  <SheetFooter className="border-border border-t p-5">
                    {setupStep === 'scan' ? (
                      <Button onClick={() => setSetupStep('verify')}>Continuer</Button>
                    ) : null}
                    {setupStep === 'verify' ? (
                      <Button disabled={totp.length !== 6} onClick={() => setSetupStep('backup')}>
                        Vérifier le code
                      </Button>
                    ) : null}
                    {setupStep === 'backup' ? (
                      <SheetClose asChild>
                        <Button>Terminer</Button>
                      </SheetClose>
                    ) : null}
                    <SheetClose asChild>
                      <Button variant="outline">Annuler</Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>

              <Sheet open={resetSheetOpen} onOpenChange={setResetSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline">
                    <RefreshCcw className="size-4" />
                    Réinitialiser MFA
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
                  <SheetHeader className="border-border border-b p-5 pr-12">
                    <SheetTitle>Réinitialiser le MFA</SheetTitle>
                    <SheetDescription>
                      Cette demande désactivera la configuration MFA actuelle après validation interne.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="grid gap-4 p-5">
                    <WarningNotice title="Action sensible">
                      Une vérification interne est nécessaire avant de permettre une nouvelle configuration.
                    </WarningNotice>
                    <ReasonTextarea
                      value={resetReason}
                      onChange={(event) => setResetReason(event.target.value)}
                      aria-label="Motif de réinitialisation MFA"
                    />
                  </div>
                  <SheetFooter className="border-border border-t p-5">
                    <Button
                      variant="destructive"
                      disabled={resetReason.trim().length < 10}
                      onClick={() => {
                        toast.success('Réinitialisation MFA demandée', {
                          description:
                            'Demande transmise à un administrateur — action journalisée.',
                        });
                        setResetSheetOpen(false);
                      }}
                    >
                      <RefreshCcw className="size-4" />
                      Demander la réinitialisation
                    </Button>
                    <SheetClose asChild>
                      <Button variant="outline">Annuler</Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          }
        />

        <AccountNav />

        <section className="grid gap-4 md:grid-cols-3">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Statut MFA</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Badge className="w-fit">Actif</Badge>
              <p className="text-muted-foreground text-sm leading-6">
                Un code TOTP est demandé après le mot de passe pour ouvrir la console.
              </p>
              {mfaMandatory ? (
                <p className="text-status-failed-800 text-xs leading-5">
                  MFA obligatoire pour votre rôle financier — il ne peut pas être désactivé.
                </p>
              ) : null}
            </CardContent>
          </Card>
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Application principale</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <p className="text-quipux-navy-950 font-medium">Google Authenticator</p>
              <p className="text-muted-foreground">Dernière vérification : 14 juil. 2026, 08:15</p>
            </CardContent>
          </Card>
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Codes de secours</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <p className="text-quipux-navy-950 text-2xl font-semibold">{BACKUP_CODES.length}</p>
              <p className="text-muted-foreground">Codes disponibles pour récupération exceptionnelle.</p>
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}
